import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import type { User } from '@reflex/shared';
import { OrgOnboardingProfileSchema } from '@reflex/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@reflex/ui/components/ui/dialog';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { randomClientId } from '@reflex/ui/lib/random-id';
import { Switch } from '@reflex/ui/components/ui/switch';
import { Label } from '@reflex/ui/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@reflex/ui/components/ui/tabs';
import { FormField } from '@/components/forms/FormField';
import { MemberPicker } from '@/components/members/MemberPicker';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { EmailDomainsField } from '@/components/auth/EmailDomainsField';
import { CapabilitiesField } from '@/components/admin/CapabilitiesField';
import { OrgOnboardingProfileSelector } from '@/components/admin/OrgOnboardingProfileSelector';
import { PluginBootstrapErrors } from '@/components/admin/plugin-bootstrap/PluginBootstrapErrors';
import { useAuth } from '@/hooks/auth/useAuth';
import {
  extractPluginBootstrapErrorDetail,
  type PluginBootstrapError,
} from '@/components/admin/plugin-bootstrap/PluginBootstrapError';
import { ApiError, api } from '@/lib/api';
import { ROUTES } from '@/lib/routes';
import { queryKeys } from '@/lib/query-keys';
import { getUserDisplayName } from '@/lib/agent-utils';
import { useFeatureFlag } from '@/hooks/config/useFeatureFlag';

/**
 * Inline message shown on the email field when the server reports the pending
 * owner's email already belongs to a user (a 409 with
 * `code: 'email_already_registered'` from the atomic org-create call).
 */
const PENDING_OWNER_EMAIL_TAKEN_MESSAGE =
  'A user with this email already exists. Switch to Existing user and pick them.';

const pendingOwnerEmailSchema = z.string().email();

const formSchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
    slug: z
      .string()
      .max(60, 'URL slug is too long')
      .regex(/^[a-z0-9-]*$/, 'URL slug can only contain lowercase letters, numbers, and hyphens')
      .optional()
      .or(z.literal('')),
    // 'existing' picks the owner from the user list; 'new' has the server
    // pre-provision a pending user by email (atomically with the org) and make
    // them the owner. The owner fields are validated per-mode in the
    // superRefine below.
    ownerMode: z.enum(['existing', 'new']),
    ownerUserId: z.string(),
    pendingOwnerEmail: z.string(),
    pendingOwnerName: z.string().max(100, 'Name is too long').optional().or(z.literal('')),
    supportEmail: z.string().email('Enter a valid email').optional().or(z.literal('')),
    // The server derives starting plugins from the onboarding profile.
    orgOnboardingProfile: OrgOnboardingProfileSchema.optional(),
    hasSubscription: z.boolean(),
    // Only meaningful when `hasSubscription` is on (the field is hidden
    // otherwise), but always present and valid so toggling the subscription off
    // never blocks submit. Mirrors the server-side bound (1–365 days).
    subscriptionDurationDays: z.number().int().positive().max(365),
  })
  .superRefine((values, ctx) => {
    if (values.ownerMode === 'existing' && !values.ownerUserId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ownerUserId'],
        message: 'Owner is required',
      });
    }
    if (values.ownerMode === 'new') {
      const email = values.pendingOwnerEmail.trim();
      if (!email) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pendingOwnerEmail'],
          message: 'Email is required',
        });
      } else if (!z.validate(pendingOwnerEmailSchema, email)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pendingOwnerEmail'],
          message: 'Enter a valid email',
        });
      }
    }
    if (!values.orgOnboardingProfile) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['orgOnboardingProfile'],
        message: 'Choose the kind of work this organization will do',
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

interface CreateOrganizationDialogProps {
  /**
   * Optional override for the user list. When omitted, the dialog fetches
   * users itself via `api.listUsers` on first open. Tests/stories pass an
   * explicit list to keep them deterministic.
   */
  users?: User[];
  /**
   * Render the trigger button inline. When false, the dialog is controlled
   * via `open`/`onOpenChange` and no trigger is rendered. Defaults to true.
   */
  withTrigger?: boolean;
  /** Controlled open state (overrides internal state when provided). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Super-admin dialog for creating an organization on behalf of another user.
 * The owner is either picked from the existing user list or pre-provisioned
 * by email (for someone who has never signed in — they claim the account on
 * their first verified sign-in). The owner receives the `org:owner` role;
 * the admin pressing the button is recorded as `createdByUserId` for audit
 * but is not added as a member of the new org.
 */
export function CreateOrganizationDialog({
  users: usersOverride,
  withTrigger = true,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: CreateOrganizationDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const auth = useAuth();
  const automaticProvisioningEnabled = useFeatureFlag('runloop_account_provisioning_enabled');
  const usersQuery = useQuery({
    queryKey: queryKeys.adminUsers,
    queryFn: () => api.listUsers(),
    enabled: usersOverride === undefined && open,
  });
  const users = usersOverride ?? usersQuery.data ?? [];
  const usersById = new Map(users.map((u) => [u.id, u] as const));

  // The operator no longer picks plugins here: the form sends only the
  // onboarding profile (below) and the server derives the plugin selection
  // from it, matching the self-service wizard. A bootstrap error only appears if
  // the server rejects that derived selection.
  const [bootstrapError, setBootstrapError] = useState<PluginBootstrapError | undefined>();

  const [emailDomains, setEmailDomains] = useState<string[]>([]);
  const [provisioningIdempotencyKey, setProvisioningIdempotencyKey] = useState(randomClientId);
  // Capability slugs granted at create time. Like `emailDomains` and the
  // plugin selection, this lives outside React Hook Form because it is a
  // dynamic add/remove list editor, not a single validated field.
  const [capabilities, setCapabilities] = useState<string[]>([]);

  const DEFAULT_SUBSCRIPTION_DAYS = 14;

  const defaultFormValues: FormValues = {
    name: '',
    slug: '',
    ownerMode: 'existing',
    ownerUserId: '',
    pendingOwnerEmail: '',
    pendingOwnerName: '',
    supportEmail: '',
    orgOnboardingProfile: undefined,
    hasSubscription: false,
    subscriptionDurationDays: DEFAULT_SUBSCRIPTION_DAYS,
  };

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultFormValues,
  });

  // Reset the form whenever the dialog closes so a re-open starts clean.
  useEffect(() => {
    try {
      window.localStorage.removeItem('reflex.admin.pending-trial-provision.v1');
    } catch {
      // Organization creation also works when browser storage is unavailable.
    }
    if (!open) {
      reset(defaultFormValues);
      setEmailDomains([]);
      setCapabilities([]);
      setBootstrapError(undefined);
      setProvisioningIdempotencyKey(randomClientId());
    }
    // defaultFormValues is rebuilt each render but structurally constant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  // eslint-disable-next-line react-hooks/incompatible-library -- React Hook Form's watch() is safe here; React Compiler compat is a known limitation
  const ownerUserId = watch('ownerUserId');
  const ownerMode = watch('ownerMode');
  const owner = ownerUserId ? usersById.get(ownerUserId) : undefined;
  const orgOnboardingProfile = watch('orgOnboardingProfile');
  // A time-limited subscription is independent of organization setup.
  const hasSubscription = watch('hasSubscription');

  const createMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const subscription = values.hasSubscription
        ? { type: 'trial' as const, durationDays: values.subscriptionDurationDays }
        : undefined;

      // Owner is either an existing pick (`ownerUserId`) or a pending user the
      // server pre-provisions by email INSIDE the org-create transaction
      // (`pendingOwner`). One request, one transaction: a failed create rolls
      // the pending user back too, so there is no orphaned account and no
      // client-side orchestration to unwind.
      const isPendingOwner = values.ownerMode === 'new';
      const ownerFields = isPendingOwner
        ? {
            pendingOwner: {
              email: values.pendingOwnerEmail.trim(),
              ...(values.pendingOwnerName?.trim() ? { name: values.pendingOwnerName.trim() } : {}),
            },
          }
        : { ownerUserId: values.ownerUserId };

      const organizationInput = {
        name: values.name,
        slug: values.slug ? values.slug : undefined,
        ...ownerFields,
        ...(values.supportEmail ? { supportEmail: values.supportEmail } : {}),
        ...(emailDomains.length > 0 ? { emailDomains } : {}),
        ...(values.orgOnboardingProfile
          ? { orgOnboardingProfile: values.orgOnboardingProfile }
          : {}),
        ...(capabilities.length > 0 ? { capabilities } : {}),
        ...(subscription ? { subscription } : {}),
      };
      const org = automaticProvisioningEnabled
        ? await api.adminCreateProvisionedOrganization({
            idempotencyKey: provisioningIdempotencyKey,
            ...organizationInput,
          })
        : await api.adminCreateOrganization(organizationInput);
      return { org, isPendingOwner, ownerUserId: values.ownerUserId };
    },
    onSuccess: async (outcome) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizations });
      // A pre-provisioned owner is a new row in the admin user list and the
      // member pickers — refresh so it shows up. Only on success: a failed
      // create rolled the pending user back, so nothing changed.
      if (outcome.isPendingOwner) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
      }

      setOpen(false);
      reset(defaultFormValues);
      setEmailDomains([]);
      setCapabilities([]);
      setBootstrapError(undefined);
      setProvisioningIdempotencyKey(randomClientId());

      toast.success('Organization created', { description: outcome.org.name });
      // Only the owner becomes a member of the new org. When the admin
      // makes themselves the owner, bounce into the per-org setup wizard
      // so the brand-new org never sits un-bootstrapped — but refresh
      // memberships FIRST so the wizard route resolves the new slug
      // against fresh auth state instead of a stale snapshot (which would
      // flash "Organization not found"). When the org belongs to someone
      // else the admin will never be a member, so stay on the admin page. A
      // pre-provisioned pending owner is never the admin, so stay put.
      if (outcome.isPendingOwner || outcome.ownerUserId !== auth.user?.id) return;
      await auth.refreshSession();
      navigate(ROUTES.org(outcome.org.slug).setup);
    },
    onError: (err) => {
      // The atomic create returns a 409 with `email_already_registered` when a
      // `pendingOwner` email is taken. Route it to the email field (not a toast)
      // and keep the dialog open so the admin can switch to Existing user. The
      // `code` check avoids catching the surface's other 409s (email-domain
      // claims, provisioning-in-progress).
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        err.code === 'email_already_registered'
      ) {
        setError('pendingOwnerEmail', {
          type: 'server',
          message: PENDING_OWNER_EMAIL_TAKEN_MESSAGE,
        });
        return;
      }
      // 400 responses with structured plugin details bubble back into the
      // form as inline guidance instead of a toast because the plugin slugs
      // are the actionable bit.
      const detail =
        err instanceof ApiError && err.status === 400
          ? extractPluginBootstrapErrorDetail(err.body)
          : undefined;
      if (detail) {
        setBootstrapError(detail);
        return;
      }
      setBootstrapError(undefined);
      toast.error('Failed to create organization', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  function onSubmit(values: FormValues) {
    setBootstrapError(undefined);
    createMutation.mutate(values);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {withTrigger && (
        <DialogTrigger asChild>
          <Button
            type="button"
            size="sm"
            data-testid="admin-create-organization-button"
            aria-label="Create organization"
          >
            <Plus className="icon-sm" />
            Create organization
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>
            Create a new organization and assign an owner. Pick an existing user or create one by
            email; either way they receive the organization owner role.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormField label="Name" htmlFor="org-name" required error={errors.name?.message}>
            <Input
              id="org-name"
              autoFocus
              placeholder="Acme Inc."
              data-testid="admin-create-organization-name"
              {...register('name')}
            />
          </FormField>

          <FormField
            label="URL slug"
            htmlFor="org-slug"
            hint="Optional. Used in the organization URL (e.g. /acme). Auto-generated from the name if left blank."
            error={errors.slug?.message}
          >
            <Input
              id="org-slug"
              placeholder="acme"
              data-testid="admin-create-organization-slug"
              {...register('slug')}
            />
          </FormField>

          <FormField
            label="Owner"
            required
            error={ownerMode === 'existing' ? errors.ownerUserId?.message : undefined}
          >
            <Tabs
              value={ownerMode}
              onValueChange={(value) => {
                setValue('ownerMode', value as FormValues['ownerMode']);
                // Each mode validates its own fields; drop the other
                // mode's stale errors so switching clears the noise.
                clearErrors(['ownerUserId', 'pendingOwnerEmail']);
              }}
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger
                  value="existing"
                  data-testid="admin-create-organization-owner-mode-existing"
                >
                  Existing user
                </TabsTrigger>
                <TabsTrigger value="new" data-testid="admin-create-organization-owner-mode-new">
                  New user
                </TabsTrigger>
              </TabsList>
              <TabsContent value="existing" className="mt-2">
                <Controller
                  name="ownerUserId"
                  control={control}
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <div>
                        <MemberPicker
                          users={users}
                          onSelect={(userId) => {
                            field.onChange(userId);
                            setValue('ownerUserId', userId, { shouldValidate: true });
                          }}
                          triggerLabel={owner ? 'Change owner' : 'Pick owner'}
                          ariaLabel="Pick organization owner"
                          searchPlaceholder="Search users..."
                        />
                      </div>
                      {owner && (
                        <div
                          className="flex items-center gap-2 rounded-md border bg-muted/40 p-2"
                          data-testid="admin-create-organization-selected-owner"
                        >
                          <UserAvatar
                            userName={getUserDisplayName(owner)}
                            avatarUrl={owner.avatarUrl ?? undefined}
                            size="sm"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">
                              {getUserDisplayName(owner)}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">@{owner.login}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                />
              </TabsContent>
              <TabsContent value="new" className="mt-2 space-y-3">
                <p className="text-xs text-muted-foreground">
                  Creates an account for someone who has not signed in yet. They become the owner
                  now and claim the account the first time they sign in with this email.
                </p>
                <FormField
                  label="Email"
                  htmlFor="org-pending-owner-email"
                  required
                  error={errors.pendingOwnerEmail?.message}
                >
                  <Input
                    id="org-pending-owner-email"
                    type="email"
                    placeholder="owner@acme.com"
                    autoComplete="off"
                    data-testid="admin-create-organization-pending-owner-email"
                    {...register('pendingOwnerEmail')}
                  />
                </FormField>
                <FormField
                  label="Full name"
                  htmlFor="org-pending-owner-name"
                  hint="Optional."
                  error={errors.pendingOwnerName?.message}
                >
                  <Input
                    id="org-pending-owner-name"
                    placeholder="Ada Lovelace"
                    data-testid="admin-create-organization-pending-owner-name"
                    {...register('pendingOwnerName')}
                  />
                </FormField>
              </TabsContent>
            </Tabs>
          </FormField>

          <FormField
            label="Support email"
            htmlFor="org-support-email"
            hint="Shown to users whose email domain isn't claimed by any org. Optional."
            error={errors.supportEmail?.message}
          >
            <Input
              id="org-support-email"
              type="email"
              placeholder="support@acme.com"
              autoComplete="off"
              data-testid="admin-create-organization-support-email"
              {...register('supportEmail')}
            />
          </FormField>

          <FormField
            label="Email domains"
            hint="Users logging in with one of these domains who have no other org will auto-join as a member. Each domain must be unique across orgs."
          >
            <EmailDomainsField
              value={emailDomains}
              onChange={setEmailDomains}
              disabled={createMutation.isPending}
              testIdPrefix="admin-create-organization-email-domains"
            />
          </FormField>

          <FormField
            label="Work type"
            labelId="org-kind-label"
            required
            hint="What kind of work will this organization use Reflex for? This sets up relevant plugins, which can be changed after setup."
            error={errors.orgOnboardingProfile?.message}
          >
            <OrgOnboardingProfileSelector
              value={orgOnboardingProfile}
              onChange={(next) =>
                setValue('orgOnboardingProfile', next, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              labelId="org-kind-label"
              required
              testIdPrefix="admin-create-organization-kind"
            />
          </FormField>

          {bootstrapError && <PluginBootstrapErrors error={bootstrapError} />}

          <FormField
            label="Capabilities"
            hint="Free-form capability slugs that unlock plugins whose required capabilities match. Optional; platform admins can edit these later."
          >
            <CapabilitiesField
              value={capabilities}
              onChange={setCapabilities}
              disabled={createMutation.isPending}
              testIdPrefix="admin-create-organization-capabilities"
            />
          </FormField>

          <div className="space-y-3 rounded-md border p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Label htmlFor="org-has-subscription" className="font-medium">
                  Subscription
                </Label>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Time-limited access. When the subscription window ends, members are blocked with a
                  "trial has ended" screen until a super-admin extends or clears it.
                </p>
              </div>
              <Controller
                name="hasSubscription"
                control={control}
                render={({ field }) => (
                  <Switch
                    id="org-has-subscription"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    aria-label="Subscription"
                    data-testid="admin-create-organization-subscription-toggle"
                  />
                )}
              />
            </div>
            {hasSubscription && (
              <FormField
                label="Subscription length (days)"
                htmlFor="org-subscription-days"
                error={errors.subscriptionDurationDays?.message}
              >
                <Input
                  id="org-subscription-days"
                  type="number"
                  min={1}
                  max={365}
                  className="w-32"
                  data-testid="admin-create-organization-subscription-days"
                  {...register('subscriptionDurationDays', { valueAsNumber: true })}
                />
              </FormField>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="admin-create-organization-submit"
              disabled={createMutation.isPending || isSubmitting}
            >
              {createMutation.isPending ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
